import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Calculator, TrendingDown, TrendingUp, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, http } from "@/lib/api";
import { fmtSignedUsd, fmtUsd, colorForPnl } from "@/lib/format";
import ProfitPlanner from "@/components/ProfitPlanner";
import CoinIcon from "@/components/CoinIcon";

const simulate = (payload) => http.post("/simulate", payload).then((r) => r.data);

const defaults = {
  symbol: "ETHUSDT",
  counter_exchange: "OKX",
  amount_usd: 1000,
  leverage: 5,
  entry_price_darkex: "",
  entry_price_counter: "",
  exit_price_darkex: "",
  exit_price_counter: "",
  hold_seconds: 600,
  funding_fees: 0,
  extra_fees: 0,
};

export default function Scenario() {
  const [form, setForm] = useState(defaults);
  const [result, setResult] = useState(null);
  const [searchParams] = useSearchParams();

  // Yeni Hedge modal'dan gelen "Önce Simüle Et" linkinden parametreleri al
  useEffect(() => {
    const symbol = searchParams.get("symbol");
    const counter = searchParams.get("counter");
    const amount = searchParams.get("amount");
    const leverage = searchParams.get("leverage");
    if (symbol || counter || amount || leverage) {
      setForm((f) => ({
        ...f,
        ...(symbol ? { symbol } : {}),
        ...(counter ? { counter_exchange: counter } : {}),
        ...(amount ? { amount_usd: amount } : {}),
        ...(leverage ? { leverage } : {}),
        // Yeni semboldeysek giriş/çıkış fiyatlarını sıfırla (canlı al)
        entry_price_darkex: "",
        entry_price_counter: "",
        exit_price_darkex: "",
        exit_price_counter: "",
      }));
      const target = searchParams.get("target");
      if (target) {
        toast.info(
          `Yeni Hedge'den geldin · Hedef kâr $${target} — `
          + "fiyat hareketi senaryolarını dene",
          { duration: 4000 },
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const { data: prices } = useQuery({
    queryKey: ["prices", form.symbol],
    queryFn: () => api.prices(form.symbol),
    refetchInterval: 8000,
  });
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: api.getSettings,
  });
  const { data: meta } = useQuery({
    queryKey: ["meta"],
    queryFn: api.meta,
  });

  // Auto-populate live entry prices
  useEffect(() => {
    if (prices && !form.entry_price_darkex && !form.entry_price_counter) {
      const cKey = form.counter_exchange.toLowerCase();
      setForm((f) => ({
        ...f,
        entry_price_darkex: prices.darkex?.price?.toFixed(2) || "",
        entry_price_counter: prices[cKey]?.price?.toFixed(2) || "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prices, form.counter_exchange]);

  const useLive = () => {
    const cKey = form.counter_exchange.toLowerCase();
    setForm({
      ...form,
      entry_price_darkex: prices?.darkex?.price?.toFixed(2) || "",
      entry_price_counter: prices?.[cKey]?.price?.toFixed(2) || "",
    });
  };

  const applyScenario = (slPct) => {
    // Suggest exit prices: market moved UP by slPct (Darkex loses, counter wins)
    const ed = parseFloat(form.entry_price_darkex);
    const ec = parseFloat(form.entry_price_counter);
    if (!ed || !ec) return;
    setForm({
      ...form,
      exit_price_darkex: (ed * (1 + slPct / 100)).toFixed(2),
      exit_price_counter: (ec * (1 + slPct / 100)).toFixed(2),
    });
  };

  const mut = useMutation({
    mutationFn: simulate,
    onSuccess: (d) => {
      setResult(d);
      toast.success(`Net K/Z: ${fmtSignedUsd(d.net_pnl)}`);
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail || e?.message || e;
      toast.error(`Simülasyon hatası: ${detail}`);
    },
  });

  const run = () => {
    const payload = {
      symbol: form.symbol,
      counter_exchange: form.counter_exchange,
      amount_usd: parseFloat(form.amount_usd),
      leverage: parseFloat(form.leverage),
      entry_price_darkex: parseFloat(form.entry_price_darkex) || null,
      entry_price_counter: parseFloat(form.entry_price_counter) || null,
      exit_price_darkex: parseFloat(form.exit_price_darkex),
      exit_price_counter: parseFloat(form.exit_price_counter),
      hold_seconds: parseInt(form.hold_seconds, 10),
      funding_fees: parseFloat(form.funding_fees) || 0,
      extra_fees: parseFloat(form.extra_fees) || 0,
    };
    if (!payload.exit_price_darkex || !payload.exit_price_counter) {
      toast.error("Çıkış fiyatlarını girin (veya 'Senaryo Uygula' butonunu kullanın)");
      return;
    }
    mut.mutate(payload);
  };

  return (
    <div className="p-5 space-y-4 max-w-6xl">
      {/* Kâr Planlayıcı — dene-yanıl + projeksiyonlar */}
      <ProfitPlanner />

      {/* Intro — Senaryo Hesaplayıcı header */}
      <section className="tx-card">
        <span className="tx-cb3" />
        <span className="tx-cb4" />
        <div className="tx-ch-head">
          <div className="tx-ch-ic" aria-hidden>
            <svg viewBox="0 0 24 24">
              <rect x="4" y="3" width="16" height="18" rx="2" />
              <path d="M8 7h8M8 11h8M8 15h4" strokeLinecap="round" />
            </svg>
          </div>
          <div className="tx-ch-tt">
            <h2>Senaryo Hesaplayıcı</h2>
            <div className="sub">Net kar/zarar simülasyonu — gerçek emir gönderilmez</div>
          </div>
        </div>
        <div className="tx-card-b">
          <p className="text-xs text-zinc-400" style={{ marginBottom: 10 }}>
            Şu tutarla girersem, şu fiyatta çıkarsam — Darkex bonus + iki tarafın
            ücretleri + funding dahil <b className="text-white">net kar/zararım ne olur</b>?
          </p>
          {settings && (
            <div className="text-xs text-zinc-500"
                 style={{
                   display: "flex", flexWrap: "wrap", gap: "10px 18px",
                   padding: "10px 14px", borderRadius: 8,
                   background: "rgba(255,93,21,0.04)",
                   border: "1px solid rgba(255,93,21,0.12)",
                 }}>
              <span>Aktif tier: <b className="text-[#ff5d15] font-semibold">{settings.tier}</b></span>
              <span>Bonus oranı: <b className="text-[#ff5d15]">%{Math.round(settings.default_bonus_rate * 100)}</b></span>
              <span>Komisyon Darkex: <b className="text-white">%{(settings.default_darkex_fee_rate * 100).toFixed(3)}</b></span>
              <span>Komisyon Karşı: <b className="text-white">%{(settings.default_counter_fee_rate * 100).toFixed(3)}</b></span>
            </div>
          )}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Inputs */}
        <Panel
          title="Senaryo Parametreleri"
          icon={
            <svg viewBox="0 0 24 24">
              <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />
            </svg>
          }
        >
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sembol">
              <div className="flex items-center gap-2">
                <CoinIcon symbol={form.symbol} size="md" />
                <Select value={form.symbol}
                  onValueChange={(v) => setForm({
                    ...form, symbol: v,
                    entry_price_darkex: "", entry_price_counter: "",
                    exit_price_darkex: "", exit_price_counter: "",
                  })}>
                  <SelectTrigger data-testid="sim-symbol"
                    className="bg-transparent border-[#27272A] h-9 rounded-sm font-mono flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#27272A] rounded-sm text-white">
                    {(meta?.pairs || ["ETHUSDT"]).map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </Field>
            <Field label="Karşı Borsa">
              <Select value={form.counter_exchange}
                onValueChange={(v) => setForm({ ...form, counter_exchange: v })}>
                <SelectTrigger data-testid="sim-counter"
                  className="bg-transparent border-[#27272A] h-9 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#27272A] rounded-sm text-white">
                  <SelectItem value="OKX">OKX</SelectItem>
                  <SelectItem value="BINANCE">Binance</SelectItem>
                  <SelectItem value="BYBIT">Bybit (simülasyon)</SelectItem>
                  <SelectItem value="BITGET">Bitget (simülasyon)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Tutar (USD)">
              <Input data-testid="sim-amount" type="number" step="50"
                value={form.amount_usd}
                onChange={(e) => setForm({ ...form, amount_usd: e.target.value })}
                className="bg-transparent border-[#27272A] h-9 rounded-sm font-mono" />
            </Field>
            <Field label="Kaldıraç (x)">
              <Input data-testid="sim-leverage" type="number" step="0.5"
                value={form.leverage}
                onChange={(e) => setForm({ ...form, leverage: e.target.value })}
                className="bg-transparent border-[#27272A] h-9 rounded-sm font-mono" />
            </Field>
            <Field label={<span className="flex items-center gap-1"><TrendingDown className="w-3 h-3 text-[#ff5662]" />Darkex Giriş</span>}>
              <Input data-testid="sim-entry-d" type="number" step="0.01"
                value={form.entry_price_darkex}
                onChange={(e) => setForm({ ...form, entry_price_darkex: e.target.value })}
                className="bg-transparent border-[#ff5662]/30 h-9 rounded-sm font-mono" />
            </Field>
            <Field label={<span className="flex items-center gap-1"><TrendingUp className="w-3 h-3 text-[#2bd98a]" />Karşı Giriş</span>}>
              <Input data-testid="sim-entry-c" type="number" step="0.01"
                value={form.entry_price_counter}
                onChange={(e) => setForm({ ...form, entry_price_counter: e.target.value })}
                className="bg-transparent border-[#2bd98a]/30 h-9 rounded-sm font-mono" />
            </Field>
            <Field label="Darkex Çıkış">
              <Input data-testid="sim-exit-d" type="number" step="0.01"
                value={form.exit_price_darkex}
                onChange={(e) => setForm({ ...form, exit_price_darkex: e.target.value })}
                className="bg-transparent border-[#ff5662]/30 h-9 rounded-sm font-mono" />
            </Field>
            <Field label="Karşı Çıkış">
              <Input data-testid="sim-exit-c" type="number" step="0.01"
                value={form.exit_price_counter}
                onChange={(e) => setForm({ ...form, exit_price_counter: e.target.value })}
                className="bg-transparent border-[#2bd98a]/30 h-9 rounded-sm font-mono" />
            </Field>
            <Field label="Pozisyon Süresi (sn)">
              <Input data-testid="sim-hold" type="number" step="60"
                value={form.hold_seconds}
                onChange={(e) => setForm({ ...form, hold_seconds: e.target.value })}
                className="bg-transparent border-[#27272A] h-9 rounded-sm font-mono" />
            </Field>
            <Field label="Funding Ücreti (USD)">
              <Input data-testid="sim-funding" type="number" step="0.01"
                value={form.funding_fees}
                onChange={(e) => setForm({ ...form, funding_fees: e.target.value })}
                className="bg-transparent border-[#27272A] h-9 rounded-sm font-mono" />
            </Field>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button data-testid="sim-use-live" variant="ghost" onClick={useLive}
              className="text-[#0A84FF] hover:bg-[#0A84FF]/10 rounded-sm h-8 text-xs">
              Canlı giriş fiyatlarını kullan
            </Button>
            {[1, 2, 3, 5].map((p) => (
              <Button key={p} variant="ghost"
                data-testid={`sim-scenario-${p}`}
                onClick={() => applyScenario(p)}
                className="text-zinc-400 hover:bg-[#1A1A1A] rounded-sm h-8 text-xs">
                Fiyat +%{p} yukarı
              </Button>
            ))}
          </div>

          <Button data-testid="sim-run" onClick={run} disabled={mut.isPending}
            className="mt-3 w-full bg-[#ff5d15] hover:bg-[#f0b90b] text-black rounded-sm h-9 font-semibold">
            {mut.isPending ? "Hesaplanıyor…" : "Hesapla"}
          </Button>
        </Panel>

        {/* Result */}
        <Panel
          title="Sonuç"
          icon={
            <svg viewBox="0 0 24 24">
              <path d="M3 3v18h18" />
              <path d="M7 13l3 3 7-8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          }
        >
          {result ? (
            <div className="space-y-3">
              <div className="bg-transparent border border-[#27272A] rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                  Net Gerçekleşen K/Z
                </div>
                <div
                  data-testid="sim-net"
                  className={`text-3xl font-mono font-bold mt-1 ${colorForPnl(result.net_pnl)}`}>
                  {fmtSignedUsd(result.net_pnl)}
                </div>
                <div className={`text-xs mt-1 ${result.bonus_eligible ? "text-[#2bd98a]" : "text-[#ff5d15]"}`}>
                  {result.bonus_eligible
                    ? "✅ Kampanya uygun · bonus alınabilir"
                    : "⚠️ Kampanya uygun değil (Darkex'te zarar yok veya <$10 ya da süre <5 dk)"}
                </div>
              </div>

              <Row label="Boyut" value={`${result.size_eth?.toFixed(4)} ETH · $${result.notional_usd?.toFixed(0)}`} />
              <Row label="Kaldıraç" value={`${result.leverage}x`} />
              <hr className="border-[#1A1A1A]" />
              <Row label="📉 Darkex K/Z (short)" value={fmtSignedUsd(result.darkex_pnl)}
                   color={colorForPnl(result.darkex_pnl)} />
              <Row label="📈 Karşı K/Z (long)" value={fmtSignedUsd(result.counter_pnl)}
                   color={colorForPnl(result.counter_pnl)} />
              <Row label="Brüt K/Z" value={fmtSignedUsd(result.gross_pnl)}
                   color={colorForPnl(result.gross_pnl)} />
              <hr className="border-[#1A1A1A]" />
              <Row label={`🎁 Bonus kredisi (%${Math.round(result.bonus_rate*100)})`}
                   value={fmtUsd(result.bonus_credit)} color="text-[#ff5d15]" />
              <Row label="💸 Toplam ücretler" value={fmtUsd(result.total_fees)}
                   color="text-zinc-400" />
            </div>
          ) : (
            <div className="text-center py-12 text-sm text-zinc-500">
              <Calculator className="w-8 h-8 mx-auto text-zinc-600 mb-3" />
              Parametreleri girip <b>Hesapla</b>'ya bas.
              <p className="text-xs text-zinc-600 mt-2">
                Hızlı senaryo için "Fiyat +%N yukarı" butonlarını kullan —
                Darkex zarar, karşı bacak kar senaryosunu otomatik kurar.
              </p>
            </div>
          )}
        </Panel>
      </div>

      <div className="bg-transparent border border-[#ff5d15]/30 rounded-md p-3 text-xs text-zinc-400 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-[#ff5d15] shrink-0 mt-0.5" />
        <div>
          <b className="text-[#ff5d15]">Bilgi:</b> Telegram'dan da
          <code className="bg-[#1A1A1A] px-1 mx-1 rounded">/simule 1000 5x sl3</code>
          yazarak aynı hesabı yapabilirsin — sonuç anlık olarak gelir.
        </div>
      </div>
    </div>
  );
}

const Panel = ({ title, icon, children }) => (
  <section className="tx-card">
    <span className="tx-cb3" />
    <span className="tx-cb4" />
    <div className="tx-ch-head">
      {icon && (
        <div className="tx-ch-ic" aria-hidden>
          {icon}
        </div>
      )}
      <div className="tx-ch-tt">
        <h2 style={{ fontSize: 13, textTransform: "uppercase",
                     letterSpacing: "0.18em", color: "#a1a8b3" }}>{title}</h2>
      </div>
    </div>
    <div className="tx-card-b">{children}</div>
  </section>
);

const Field = ({ label, children }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">
      {label}
    </Label>
    {children}
  </div>
);

const Row = ({ label, value, color = "text-zinc-300" }) => (
  <div className="flex justify-between items-baseline text-sm">
    <span className="text-xs text-zinc-500">{label}</span>
    <span className={`font-mono ${color}`}>{value}</span>
  </div>
);
